// ─────────────────────────────────────────────────────────────
// LukaDuka — Onboarding Service
// All Supabase interactions for the onboarding flow.
// No other module touches these functions.
// ─────────────────────────────────────────────────────────────

import { supabase } from '@/shared/services/supabase';
export { supabase };

// ─── 1. Create account ────────────────────────────────────────
// Registers user with Supabase Auth.
// The DB trigger (handle_new_user) auto-creates the profiles row.

export async function createAccount(
  email: string,
  password: string,
  firstName: string
): Promise<{ userId: string | null; error: string | null }> {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: { display_name: firstName },
    },
  });

  if (error) return { userId: null, error: error.message };
  if (!data.user) return { userId: null, error: 'Account creation failed.' };

  return { userId: data.user.id, error: null };
}

// ─── 2. Sign in ───────────────────────────────────────────────

export async function signIn(
  email: string,
  password: string
): Promise<{ userId: string | null; onboardingComplete: boolean; error: string | null }> {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });

  if (error) return { userId: null, onboardingComplete: false, error: error.message };
  if (!data.user) return { userId: null, onboardingComplete: false, error: 'Login failed.' };

  // Check if onboarding is complete
  const { data: profile } = await supabase
    .from('profiles')
    .select('onboarding_complete')
    .eq('id', data.user.id)
    .single();

  return {
    userId: data.user.id,
    onboardingComplete: profile?.onboarding_complete ?? false,
    error: null,
  };
}

// ─── 3. Save DOB and country ──────────────────────────────────

export async function saveProfileDetails(
  userId: string,
  firstName: string,
  dateOfBirth: string,    // ISO format: YYYY-MM-DD
  countryOfBirth: string
): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from('profiles')
    .update({
      first_name: firstName,
      display_name: firstName,
      date_of_birth: dateOfBirth,
      country_of_birth: countryOfBirth,
    })
    .eq('id', userId);

  return { error: error?.message ?? null };
}

// ─── 4. Create the birth entry ────────────────────────────────
// The first entry. Auto-generated. Marked is_system and is_first.
// Can never be deleted by the user (enforced in UI, not DB).

export async function createBirthEntry(
  userId: string,
  dateOfBirth: string,
  countryOfBirth: string
): Promise<{ error: string | null }> {
  const { error } = await supabase.from('entries').insert({
    user_id: userId,
    title: 'I was born',
    description: 'The beginning of your story.',
    entry_date: dateOfBirth,
    status: 'published',
    categories: ['memories', 'family'],
    primary_category: 'memories',
    actual_address: countryOfBirth,
    is_first: true,
    is_system: true,
  });

  return { error: error?.message ?? null };
}

// ─── 5. Complete onboarding ───────────────────────────────────
// Flips the flag. Router reads this on every login.

export async function completeOnboarding(
  userId: string
): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from('profiles')
    .update({ onboarding_complete: true })
    .eq('id', userId);

  return { error: error?.message ?? null };
}

// ─── 6. Get current session user ─────────────────────────────

export async function getCurrentUser() {
  const { data } = await supabase.auth.getUser();
  return data?.user ?? null;
}

// ─── 7. Load profile - two outcomes only (Bug P20) ────────────
// Every authenticated user has a profiles row created at signup -
// onboarding only ever UPDATEs it, never inserts it. So a missing row
// is NEVER evidence of a new user; it means the session wasn't ready
// yet or RLS hid it (e.g. before the session is warm, or an MFA
// session not yet at AAL2). Treating that the same as "genuinely no
// profile" is what let a failed load route an already-onboarded user
// back through the date-of-birth screen - completing it would have
// created a duplicate birth record. So there are only two outcomes:
// a row came back, or it didn't (for ANY reason, including the
// PostgREST "no rows" code PGRST116) - and the second one is always
// 'failed', never a signal to onboard.

export interface Profile {
  id: string;
  onboarding_complete: boolean;
  [key: string]: unknown;
}

export type ProfileLoadResult =
  | { status: 'loaded'; profile: Profile }
  | { status: 'failed'; code: string | null };

export async function getProfile(userId: string): Promise<ProfileLoadResult> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (data) return { status: 'loaded', profile: data as Profile };

  // code kept for logging only - no personal data, just the PostgREST/
  // network error code (e.g. 'PGRST116', or null for a non-PostgREST
  // failure).
  return { status: 'failed', code: error?.code ?? null };
}

// Retries a failed load only - a row either comes back or it doesn't,
// and retrying is exactly the right response to "session not warm yet"
// or a momentary database restart (both transient). 3 attempts beyond
// the first, with increasing delay, then give up and report 'failed'.
const RETRY_DELAYS_MS = [500, 1000, 2000];

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function loadProfileWithRetry(userId: string): Promise<ProfileLoadResult> {
  let result = await getProfile(userId);

  for (const ms of RETRY_DELAYS_MS) {
    if (result.status === 'loaded') return result;
    await delay(ms);
    result = await getProfile(userId);
  }

  return result;
}
